import React, { useState } from 'react';
import { useWebRTC } from '../../hooks/useWebRTC';
import { Camera, Wifi, WifiOff, Maximize2, X, Mic, MicOff } from 'lucide-react';

function LiveCameraFeeds({ alertId }) {
  const { frontVideoRef, backVideoRef, streamStatus, streamsRef, micEnabled, toggleMic } = useWebRTC(alertId);
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      {/* Inline thumbnails in side panel */}
      <div className="video-feeds" style={{ position: 'relative' }}>
        <FeedTile label="FRONT CAM" videoRef={frontVideoRef} status={streamStatus.front_camera} />
        <FeedTile label="BACK CAM"  videoRef={backVideoRef}  status={streamStatus.back_camera} />

        {/* Expand button overlay */}
        <button
          onClick={() => setExpanded(true)}
          title="Expand cameras"
          style={{
            position: 'absolute', top: 6, right: 6, zIndex: 5,
            width: 28, height: 28, display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: 'rgba(0,0,0,0.7)',
            border: '1px solid var(--border)', borderRadius: 6,
            color: 'var(--text-primary)', cursor: 'pointer',
            backdropFilter: 'blur(6px)'
          }}
        >
          <Maximize2 size={14} />
        </button>

        {/* Mic push-to-talk button */}
        <button
          onClick={toggleMic}
          title={micEnabled ? 'Mute mic (stop talking to caller)' : 'Unmute mic (talk to caller)'}
          style={{
            position: 'absolute', top: 6, right: 42, zIndex: 5,
            width: 28, height: 28, display: 'flex', alignItems: 'center',
            justifyContent: 'center',
            background: micEnabled ? 'var(--accent-red)' : 'rgba(0,0,0,0.7)',
            border: '1px solid var(--border)', borderRadius: 6,
            color: '#fff', cursor: 'pointer',
            backdropFilter: 'blur(6px)'
          }}
        >
          {micEnabled ? <Mic size={14} /> : <MicOff size={14} />}
        </button>
      </div>

      {/* Modal overlay (sidebar still visible behind) */}
      {expanded && (
        <ModalCameraView
          streamsRef={streamsRef}
          streamStatus={streamStatus}
          onClose={() => setExpanded(false)}
        />
      )}
    </>
  );
}

function FeedTile({ label, videoRef, status }) {
  return (
    <div className="video-feed">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={!label.includes('FRONT')}
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          background: '#000',
          display: status === 'live' ? 'block' : 'none'
        }}
      />
      {status !== 'live' && <StatusOverlay status={status} small />}
      <div className="video-feed-label">
        {label}
        {status === 'live' && (
          <span style={{ marginLeft: 6, color: 'var(--accent-red)', animation: 'live-blink 1.5s ease-in-out infinite' }}>● LIVE</span>
        )}
      </div>
      <style>{`@keyframes live-blink { 0%,100% { opacity:1; } 50% { opacity:0.4; } }`}</style>
    </div>
  );
}

function StatusOverlay({ status, small }) {
  const sz = small ? 22 : 48;
  const fs = small ? 9 : 14;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      {status === 'waiting' && (<>
        <Camera size={sz} color="var(--text-muted)" />
        <span style={{ fontSize: fs, color: 'var(--text-muted)', fontFamily: 'var(--font-display)' }}>WAITING FOR STREAM</span>
      </>)}
      {status === 'connecting' && (<>
        <Wifi size={sz} color="var(--accent-amber)" style={{ animation: 'live-blink 1s infinite' }} />
        <span style={{ fontSize: fs, color: 'var(--accent-amber)', fontFamily: 'var(--font-display)' }}>CONNECTING</span>
      </>)}
      {status === 'failed' && (<>
        <WifiOff size={sz} color="var(--accent-red)" />
        <span style={{ fontSize: fs, color: 'var(--accent-red)', fontFamily: 'var(--font-display)' }}>CONNECTION FAILED</span>
      </>)}
    </div>
  );
}

function ModalCameraView({ streamsRef, streamStatus, onClose }) {
  const modalFrontRef = React.useRef(null);
  const modalBackRef = React.useRef(null);

  // Attach cached MediaStreams to the modal's own <video> elements.
  // Can't share refs with the inline tiles - one ref can only attach to
  // one DOM node. Each <video> needs its own srcObject assignment.
  React.useEffect(() => {
    const attach = (el, stream) => {
      if (el && stream && el.srcObject !== stream) {
        el.srcObject = stream;
        el.play().catch(() => {});
      }
    };
    attach(modalFrontRef.current, streamsRef?.current?.front_camera);
    attach(modalBackRef.current, streamsRef?.current?.back_camera);
  }, [streamsRef, streamStatus.front_camera, streamStatus.back_camera]);

  // Handle ESC key
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 240, right: 0, bottom: 0, // leave 240px sidebar visible
      background: 'rgba(2,6,16,0.96)',
      backdropFilter: 'blur(10px)',
      zIndex: 9999,
      display: 'flex', flexDirection: 'column',
      padding: 24
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, color: 'var(--text-primary)'
      }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: 2 }}>
            EXPANDED VIEW
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>
            🆘 LIVE CAMERA FEEDS
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', background: 'var(--accent-red)',
            border: 'none', borderRadius: 8, color: '#fff',
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
            fontFamily: 'var(--font-display)', letterSpacing: 1
          }}
        >
          <X size={16} /> CLOSE (ESC)
        </button>
      </div>

      {/* Camera grid */}
      <div style={{
        flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20,
        minHeight: 0
      }}>
        <BigFeedTile label="FRONT CAMERA" videoRef={modalFrontRef} status={streamStatus.front_camera} />
        <BigFeedTile label="BACK CAMERA"  videoRef={modalBackRef}  status={streamStatus.back_camera} />
      </div>
    </div>
  );
}

function BigFeedTile({ label, videoRef, status }) {
  return (
    <div style={{
      position: 'relative',
      background: '#000',
      border: '1px solid var(--border)',
      borderRadius: 12,
      overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={!label.includes('FRONT')}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          display: status === 'live' ? 'block' : 'none'
        }}
      />
      {status !== 'live' && <StatusOverlay status={status} />}

      {/* Label + LIVE badge */}
      <div style={{
        position: 'absolute', top: 14, left: 14,
        background: 'rgba(0,0,0,0.75)', padding: '6px 12px',
        borderRadius: 6, color: 'var(--text-primary)',
        fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
        letterSpacing: 1.5, border: '1px solid var(--border)'
      }}>
        {label}
        {status === 'live' && (
          <span style={{ marginLeft: 10, color: 'var(--accent-red)', animation: 'live-blink 1.5s ease-in-out infinite' }}>
            ● LIVE
          </span>
        )}
      </div>
    </div>
  );
}

// Only re-render if alertId changes (NOT when parent alert object is mutated by location updates)
export default React.memo(LiveCameraFeeds, (prev, next) => prev.alertId === next.alertId);
